<template>
  <main class="physical-address-page">
    <AppNavBar
      :title="t('card.fillShippingInfo')"
      :border="false"
      background-color="#ffffff"
    />

    <section v-if="cardList.length" class="card-section">
      <section>
        <h2 class="section-title">{{ t("card.cardInfoTitle") }}</h2>
      </section>
      <DesktopSwipe
        ref="swipeRef"
        class="card-swipe"
        :loop="true"
        :show-indicators="false"
        @change="onCardChange"
      >
        <van-swipe-item
          v-for="(item, index) in cardList"
          :key="item.id || index"
        >
          <article
            class="virtual-card"
            :class="{ 'is-selected': currentCardIndex === index }"
          >
            <div class="virtual-card__head">
              <div data-strong>{{ cardholderName(item) }}</div>
              <AppSvgIcon
                v-if="currentCardIndex === index"
                class="virtual-card__check"
                name="icon"
                size="16"
                color="var(--wp-color-theme)"
              />
            </div>
            <div class="virtual-card__row">
              <span>{{ t("card.cardType") }}</span>
              <div data-strong>{{ item.network || "" }}</div>
            </div>
            <div class="virtual-card__row">
              <span>{{ t("wallet.cardNumber") }}</span>
              <div data-strong>
                {{ `${item.bin || ""}-****-****-${item.last4 || ""}` }}
              </div>
            </div>
            <div class="virtual-card__row">
              <span>{{ t("card.validThroughLabel") }}</span>
              <div data-strong>{{ t("card.sameAsVirtualCard") }}</div>
            </div>
            <div class="virtual-card__row">
              <span>CVV</span>
              <div data-strong>{{ t("card.sameAsVirtualCard") }}</div>
            </div>
          </article>
        </van-swipe-item>
      </DesktopSwipe>
      <div v-if="cardList.length > 1" class="carousel-dots">
        <i
          v-for="(_, index) in cardList"
          :key="index"
          :class="{ 'is-active': currentCardIndex === index }"
          @click="swipeRef?.swipeTo?.(index)"
        />
      </div>
    </section>

    <section v-if="cardList.length" class="shipping-section">
      <h2 class="section-title">{{ t("card.shippingInfoTitle") }}</h2>
    </section>

    <section class="form-section">
      <label class="field-label">{{ t("card.receiverName") }}</label>
      <van-field
        v-model="userName"
        clearable
        class="plain-field"
        :placeholder="t('card.enterReceiverName')"
      />

      <label class="field-label">{{ t("card.countryCode") }}</label>
      <van-field
        :model-value="phoneCode"
        readonly
        clickable
        class="plain-field"
        :placeholder="t('card.selectCountryCode')"
        @click="openPicker('phone')"
      >
        <template #right-icon>
          <app-image :src="arrowDownIcon" alt="" class="field-arrow" />
        </template>
      </van-field>

      <label class="field-label">{{ t("card.phoneNumber") }}</label>
      <van-field
        v-model="phoneNum"
        type="tel"
        clearable
        class="plain-field"
        :placeholder="t('card.enterPhone')"
      />

      <label class="field-label">{{ t("card.address") }}</label>
      <van-field
        :model-value="countryName"
        readonly
        clickable
        class="plain-field"
        :placeholder="t('card.selectCountry')"
        @click="openPicker('country')"
      >
        <template #right-icon>
          <app-image :src="arrowDownIcon" alt="" class="field-arrow" />
        </template>
      </van-field>
      <van-field
        :model-value="provinceName"
        readonly
        clickable
        class="plain-field plain-field--tight"
        :placeholder="t('card.selectRegion')"
        @click="openPicker('province')"
      >
        <template #right-icon>
          <app-image :src="arrowDownIcon" alt="" class="field-arrow" />
        </template>
      </van-field>
      <van-field
        :model-value="cityName"
        readonly
        clickable
        class="plain-field plain-field--tight"
        :placeholder="t('card.selectCity')"
        @click="openPicker('city')"
      >
        <template #right-icon>
          <app-image :src="arrowDownIcon" alt="" class="field-arrow" />
        </template>
      </van-field>
      <van-field
        v-model="postalAddress"
        rows="4"
        autosize
        type="textarea"
        clearable
        class="plain-field plain-field--tight"
        :placeholder="t('card.enterAddress')"
      />

      <label class="field-label">{{ t("card.postalCode") }}</label>
      <van-field
        v-model="postalCode"
        clearable
        class="plain-field"
        :placeholder="t('card.enterPostalCode')"
      />
    </section>

    <div class="action-bar" @click="handleActionBarClick">
      <van-button
        block
        round
        type="primary"
        class="submit-btn"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="saveAndNext"
      >
        {{ t("card.saveLabel") }}
      </van-button>
    </div>

    <CountryCodePicker
      v-model="countryCodeVisible"
      :title="t('card.countryCode')"
      :selected-code="phoneCode"
      @select="selectCountryCode"
    />

    <AreaSelectorPopup
      v-model="pickerVisible"
      :title="pickerTitle"
      :items="pickerItems"
      :mode="pickerMode"
      @select="selectAction"
    />
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import DesktopSwipe from "@/components/DesktopSwipe.vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import AreaSelectorPopup from "@/view/card/components/AreaSelectorPopup.vue";
import CountryCodePicker from "@/view/card/components/CountryCodePicker.vue";
import { getLocalizedAreaName } from "@/utils/display-name";
import arrowDownIcon from "@/assets/images/kyc_select_down.png";
import {
  v1CommonCityList,
  v1CommonCountryList,
  v1CommonProvinceList,
} from "@/api/card";
import { useCardOrderStore } from "@/stores/card-order";

const { t, locale } = useI18n();
const router = useRouter();
const cardOrderStore = useCardOrderStore();

const countries = ref([]);
const provinces = ref([]);
const cities = ref([]);
const cardList = ref([]);
const currentCardIndex = ref(0);
const swipeRef = ref(null);
const selectedCountry = ref(null);
const selectedProvince = ref(null);
const selectedCity = ref(null);
const userName = ref("");
const phoneCode = ref("");
const phoneNum = ref("");
const postalAddress = ref("");
const postalCode = ref("");
const pickerType = ref("");
const pickerVisible = ref(false);
const countryCodeVisible = ref(false);
const submitting = ref(false);

const countryName = computed(
  () => getLocalizedAreaName(selectedCountry.value, locale.value) || "",
);
const provinceName = computed(
  () => getLocalizedAreaName(selectedProvince.value, locale.value) || "",
);
const cityName = computed(
  () => getLocalizedAreaName(selectedCity.value, locale.value) || "",
);
const currentCardInfo = computed(
  () => cardList.value[currentCardIndex.value] || {},
);
const pickerTitle = computed(() => {
  if (pickerType.value === "country") return t("card.selectCountryRegion");
  if (pickerType.value === "province") return t("card.selectRegion");
  return t("card.selectCity");
});

const pickerMode = computed(() =>
  pickerType.value === "country" ? "country" : "area",
);
const canSubmit = computed(
  () =>
    Boolean(String(userName.value || "").trim()) &&
    Boolean(String(phoneCode.value || "").trim()) &&
    Boolean(String(phoneNum.value || "").trim()) &&
    Boolean(selectedCountry.value) &&
    Boolean(selectedProvince.value) &&
    Boolean(selectedCity.value) &&
    Boolean(String(postalAddress.value || "").trim()) &&
    Boolean(String(postalCode.value || "").trim()),
);

const pickerItems = computed(() => {
  if (pickerType.value === "country") return countries.value;
  if (pickerType.value === "province") return provinces.value;
  return cities.value;
});

function cardholderName(item) {
  return [item?.firstName, item?.lastName].filter(Boolean).join(" ") || "--";
}

function onCardChange(index) {
  currentCardIndex.value = index;
}

async function loadCountries() {
  const result = await v1CommonCountryList({ address: "" });
  countries.value = Array.isArray(result?.list) ? result.list : [];
}

async function loadProvinces() {
  if (!selectedCountry.value?.countryId) return;
  const result = await v1CommonProvinceList({
    countryId: selectedCountry.value.countryId,
  });
  provinces.value = Array.isArray(result?.list) ? result.list : [];
}

async function loadCities() {
  if (!selectedCountry.value?.countryId || !selectedProvince.value?.provinceId)
    return;
  const result = await v1CommonCityList({
    countryId: selectedCountry.value.countryId,
    provinceId: selectedProvince.value.provinceId,
  });
  cities.value = Array.isArray(result?.list) ? result.list : [];
}

async function openPicker(type) {
  if (type === "phone") {
    countryCodeVisible.value = true;
    return;
  }
  pickerType.value = type;
  if (type === "country" && !countries.value.length) {
    await loadCountries();
  }
  if (type === "province" && !selectedCountry.value) {
    showFailToast(t("card.selectCountry"));
    return;
  }
  if (type === "city" && !selectedProvince.value) {
    showFailToast(t("card.selectRegion"));
    return;
  }
  pickerVisible.value = true;
}

function selectCountryCode(item) {
  phoneCode.value = item?.dial_code || "";
}

async function selectAction({ index }) {
  if (pickerType.value === "country") {
    selectedCountry.value = countries.value[index];
    selectedProvince.value = null;
    selectedCity.value = null;
    provinces.value = [];
    cities.value = [];
    await loadProvinces();
    return;
  }

  if (pickerType.value === "province") {
    selectedProvince.value = provinces.value[index];
    selectedCity.value = null;
    cities.value = [];
    await loadCities();
    return;
  }

  selectedCity.value = cities.value[index];
}

function handleActionBarClick() {
  if (!canSubmit.value && !submitting.value) {
    showFailToast(t("card.completeAllInfo"));
  }
}

function saveAndNext() {
  if (!canSubmit.value) {
    showFailToast(t("card.completeAllInfo"));
    return;
  }

  if (postalAddress.value.trim().length < 5) {
    showFailToast(t("card.addressMinLength"));
    return;
  }

  submitting.value = true;
  cardOrderStore.mergeDraft({
    currentCardInfo: currentCardInfo.value,
    userName: userName.value.trim(),
    phoneCode: phoneCode.value.trim(),
    phoneNum: phoneNum.value.trim(),
    country: selectedCountry.value,
    province: selectedProvince.value,
    city: selectedCity.value,
    postalAddress: postalAddress.value.trim(),
    postalCode: postalCode.value.trim(),
  });
  submitting.value = false;
  router.push("/card/physical-order/confirm");
}

onMounted(async () => {
  cardOrderStore.hydrate();
  const draft = cardOrderStore.draft;
  if (!draft?.templateId) {
    router.replace("/wallet/open-card");
    return;
  }

  cardList.value = Array.isArray(draft.virtualCardData)
    ? draft.virtualCardData
    : [];
  currentCardIndex.value = Math.max(
    0,
    cardList.value.findIndex((item) => item?.id === draft.currentCardInfo?.id),
  );
  userName.value = draft.userName || "";
  phoneCode.value = draft.phoneCode || "";
  phoneNum.value = draft.phoneNum || "";
  postalAddress.value = draft.postalAddress || "";
  postalCode.value = draft.postalCode || "";

  await loadCountries();

  if (draft.country?.countryId) {
    selectedCountry.value =
      countries.value.find(
        (item) => String(item.countryId) === String(draft.country.countryId),
      ) || draft.country;
    await loadProvinces();
  }
  if (draft.province?.provinceId && provinces.value.length) {
    selectedProvince.value =
      provinces.value.find(
        (item) => String(item.provinceId) === String(draft.province.provinceId),
      ) || draft.province;
    await loadCities();
  }
  if (draft.city?.cityId && cities.value.length) {
    selectedCity.value =
      cities.value.find(
        (item) => String(item.cityId) === String(draft.city.cityId),
      ) || draft.city;
  }
});
</script>

<style scoped lang="scss">
.physical-address-page {
  min-height: var(--app-viewport-height);
  padding: 14px 14px 34px;
  background: #ffffff;
}

.card-section {
  padding-top: 2px;
}

.section-title {
  margin: 0 0 20px;
  color: #000000;
  font-size: 18px;
  font-weight: bold;
}

.card-swipe {
  display: block;
  height: 222px;

  :deep(.desktop-swipe),
  :deep(.van-swipe),
  :deep(.van-swipe__track),
  :deep(.van-swipe-item) {
    height: 100%;
  }

  :deep(.van-swipe__indicators) {
    bottom: 0;
  }

  :deep(.van-swipe__indicator) {
    width: 8px;
    height: 8px;
    background: #d9d9d9;
    opacity: 1;
  }

  :deep(.van-swipe__indicator--active) {
    background: var(--wp-color-theme);
  }
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;

  i {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d9d9d9;
  }

  .is-active {
    background: var(--wp-color-theme);
  }
}

.virtual-card {
  box-sizing: border-box;
  height: 100%;
  margin-right: 5px;
  padding: 20px 16px;
  border: 1px solid var(--wp-color-brand-1);
  border-radius: 16px;
  background: #f4f5f6;

  &.is-selected {
    background: var(--wp-color-card-3);
  }
}

.virtual-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  [data-strong] {
    color: #222222;
    font-size: 16px;
    font-weight: 500;
  }
}

.virtual-card__check {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.virtual-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  & + & {
    margin-top: 16px;
  }

  span {
    color: #666666;
    font-size: 14px;
    line-height: 20px;
  }

  [data-strong] {
    color: #222222;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    text-align: right;
  }
}

.shipping-section {
  padding-top: 20px;
}

.form-section {
  padding-top: 2px;
}

.field-label {
  display: block;
  margin: 0 0 10px;
  color: #1f2430;
  font-size: 15px;
  font-weight: 500;
}

.plain-field {
  margin-bottom: 20px;
  overflow: hidden;
  border-radius: 12px;

  &--tight {
    margin-top: -10px;
  }

  :deep(.van-cell) {
    padding: 14px;
    background: #f4f5f6;
  }

  :deep(.van-field__control),
  :deep(.van-field__control::placeholder) {
    font-size: 14px;
  }

  :deep(textarea.van-field__control) {
    min-height: 92px !important;
  }
}

.field-arrow {
  display: block;
  width: 20px;
  height: 20px;
}

.action-bar {
  padding-top: 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
}

.submit-btn {
  // box-shadow: 0 8px 16px rgba(44, 107, 255, 0.22);
}
</style>
